import React, { useState, useRef } from 'react';
import { C } from '../tokens';
import Icon from '../components/Icon';
import { Card, Pill, SectionTitle, Breadcrumb } from '../components/UI';
import useBreakpoint from '../useBreakpoint';

/* ═══════════════════════════════════════════════════════════
   Shared Toggle Component
   ═══════════════════════════════════════════════════════════ */
function Toggle({ on, onToggle }) {
  return (
    <div onClick={onToggle} style={{
      width: 44, height: 24, borderRadius: 99, cursor: 'pointer',
      background: on ? C.primary : '#d6d3d1', transition: 'background .2s',
      position: 'relative', flexShrink: 0,
    }}>
      <div style={{
        width: 20, height: 20, borderRadius: '50%', background: '#fff',
        position: 'absolute', top: 2,
        left: on ? 22 : 2, transition: 'left .2s',
        boxShadow: '0 1px 3px rgba(0,0,0,.2)',
      }} />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Backdrop Modal
   ═══════════════════════════════════════════════════════════ */
function Backdrop({ open, onClose, children, width = 480 }) {
  if (!open) return null;
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(30,24,18,.45)',
      backdropFilter: 'blur(4px)', zIndex: 9999,
      display: 'grid', placeItems: 'center', padding: 12,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: C.w, borderRadius: 18, width: '100%', maxWidth: width,
        maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 24px 64px rgba(0,0,0,.18)',
      }}>{children}</div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   PROFILE TAB
   ═══════════════════════════════════════════════════════════ */
function ProfileTab({ mob, compact }) {
  const [fullName, setFullName]     = useState('Alex Rivera');
  const [email]                     = useState('alex.rivera@nieramind.ai');
  const [jobTitle, setJobTitle]     = useState('Senior Operations Manager');
  const [department, setDepartment] = useState('Operations');
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [saved, setSaved]           = useState(false);
  const fileRef = useRef(null);

  const handleAvatar = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setAvatarPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSave = () => { setSaved(true); setTimeout(() => setSaved(false), 2500); };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: compact ? '1fr' : '1fr 340px', gap: compact ? 16 : 24, alignItems: 'start' }}>
      {/* Left — Personal Information */}
      <div style={{ background: C.w, borderRadius: 14, border: `1px solid ${C.bdr}`, overflow: 'hidden' }}>
        <div style={{ padding: mob ? '16px 20px' : '24px 28px' }}>
          <h3 style={{ fontSize: mob ? 15 : 17, fontWeight: 700, marginBottom: 4 }}>Personal Information</h3>
          <p style={{ fontSize: 13, color: C.t2 }}>Manage your profile details and avatar.</p>
        </div>
        <div style={{ padding: mob ? '0 20px 20px' : '0 28px 28px' }}>
          {/* Avatar row */}
          <div style={{ display: 'flex', alignItems: mob ? 'flex-start' : 'center', flexDirection: mob ? 'column' : 'row', gap: mob ? 14 : 20, marginBottom: mob ? 20 : 28, padding: mob ? 14 : 20, background: '#faf9f8', borderRadius: 12 }}>
            <div style={{
              width: mob ? 56 : 72, height: mob ? 56 : 72, borderRadius: 14, overflow: 'hidden', flexShrink: 0,
              background: avatarPreview ? 'none' : `linear-gradient(135deg, ${C.primary}, ${C.amber})`,
              display: 'grid', placeItems: 'center',
            }}>
              {avatarPreview ? (
                <img src={avatarPreview} alt="Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <span style={{ color: '#fff', fontSize: 24, fontWeight: 800 }}>AR</span>
              )}
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 2 }}>Profile Picture</div>
              <p style={{ fontSize: 12, color: C.t3, marginBottom: 10 }}>PNG, JPG or GIF. Max 5MB recommended.</p>
              <div style={{ display: 'flex', gap: 8 }}>
                <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAvatar} />
                <button onClick={() => fileRef.current?.click()} style={{
                  padding: '7px 16px', borderRadius: 8, border: `1px solid ${C.bdr}`,
                  background: C.w, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                }}>Upload New</button>
                {avatarPreview && (
                  <button onClick={() => setAvatarPreview(null)} style={{
                    padding: '7px 16px', borderRadius: 8, border: `1px solid ${C.red}30`,
                    background: 'none', fontSize: 12, fontWeight: 700, color: C.red, cursor: 'pointer',
                  }}>Remove</button>
                )}
              </div>
            </div>
          </div>

          {/* Form fields */}
          <div style={{ display: 'grid', gridTemplateColumns: mob ? '1fr' : '1fr 1fr', gap: mob ? 14 : 20 }}>
            <FieldInput label="Full Name" value={fullName} onChange={setFullName} />
            <FieldInput label="Email Address" value={email} disabled icon="lock" />
            <FieldInput label="Job Title" value={jobTitle} onChange={setJobTitle} />
            <div>
              <Label>Department</Label>
              <select value={department} onChange={e => setDepartment(e.target.value)} style={{
                width: '100%', padding: '10px 14px', borderRadius: 10,
                border: `1px solid ${C.bdr}`, background: '#faf9f8',
                fontSize: 13, outline: 'none', color: C.t1, appearance: 'none',
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24'%3E%3Cpath fill='%23a8a29e' d='M7 10l5 5 5-5z'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center',
              }}>
                {['Operations', 'Engineering', 'Marketing', 'Sales', 'Support', 'Finance', 'HR'].map(d => (
                  <option key={d}>{d}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
            <button onClick={handleSave} style={{
              padding: '10px 28px', borderRadius: 10, border: 'none',
              background: saved ? C.green : C.primary, color: '#fff',
              fontSize: 13, fontWeight: 700, cursor: 'pointer', transition: 'background .2s',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <Icon name={saved ? 'check_circle' : 'save'} size={16} />
              {saved ? 'Saved!' : 'Update Profile'}
            </button>
          </div>
        </div>
      </div>

      {/* Right — Side cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* Account Verification */}
        <div style={{ background: C.w, borderRadius: 14, border: `1px solid ${C.bdr}`, padding: 24 }}>
          <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 16 }}>Account Verification</h4>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '10px 16px', borderRadius: 10, background: '#ecfdf5', marginBottom: 12,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Icon name="verified" size={22} style={{ color: C.green }} />
              <span style={{ fontSize: 13, fontWeight: 700, color: C.green }}>Identity Verified</span>
            </div>
            <Icon name="check_circle" size={18} style={{ color: C.green }} />
          </div>
          <p style={{ fontSize: 12, color: C.t3, lineHeight: 1.6 }}>
            Your identity has been successfully verified via enterprise single sign-on (SSO).
          </p>
        </div>

        {/* Organization Details */}
        <div style={{ background: C.w, borderRadius: 14, border: `1px solid ${C.bdr}`, padding: 24 }}>
          <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 16 }}>Organization Details</h4>
          {[
            ['domain', 'Organization', 'Nieramind Labs Inc.'],
            ['shield_person', 'Global Role', 'Workspace Owner'],
          ].map(([ic, lbl, val]) => (
            <div key={lbl} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: '#f5f3f1', display: 'grid', placeItems: 'center' }}>
                <Icon name={ic} size={20} style={{ color: C.t2 }} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: C.t3 }}>{lbl}</div>
                <div style={{ fontSize: 13, fontWeight: 700 }}>{val}</div>
              </div>
            </div>
          ))}
          <button style={{
            width: '100%', padding: '9px 0', borderRadius: 8,
            border: `1px solid ${C.bdr}`, background: C.w,
            fontSize: 12, fontWeight: 700, color: C.t2, cursor: 'pointer',
            marginTop: 4,
          }}>View Organization Settings</button>
        </div>

        {/* Security Note */}
        <div style={{ background: C.dark, borderRadius: 14, padding: 24, color: '#fff', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', bottom: -16, right: -16, opacity: 0.06 }}>
            <Icon name="shield" size={100} style={{ color: '#fff' }} />
          </div>
          <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>Security Note</h4>
          <p style={{ fontSize: 12, color: '#ccc', lineHeight: 1.6, marginBottom: 12 }}>
            Last login was today at 09:42 AM from San Francisco, CA.
          </p>
          <button style={{
            background: 'none', border: 'none', color: C.primary,
            fontSize: 12, fontWeight: 700, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 4, padding: 0,
          }}>
            View Login History <Icon name="arrow_forward" size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   SECURITY TAB
   ═══════════════════════════════════════════════════════════ */
function SecurityTab({ mob, compact }) {
  const [twoFA, setTwoFA]             = useState(false);
  const [curPwd, setCurPwd]           = useState('');
  const [newPwd, setNewPwd]           = useState('');
  const [confirmPwd, setConfirmPwd]   = useState('');
  const [showCur, setShowCur]         = useState(false);
  const [showNew, setShowNew]         = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pwdSaved, setPwdSaved]       = useState(false);
  const [pwdError, setPwdError]       = useState('');
  const [sessions, setSessions]       = useState([
    { id: 1, device: 'MacBook Pro', location: 'San Francisco, USA', icon: 'laptop_mac', active: true, time: 'Active Now' },
    { id: 2, device: 'iPhone 15', location: 'Jakarta, Indonesia', icon: 'smartphone', active: false, time: 'Logged in 3h ago' },
    { id: 3, device: 'Windows Desktop', location: 'Singapore', icon: 'desktop_windows', active: false, time: 'Logged in 2 days ago' },
  ]);
  const [signOutConfirm, setSignOutConfirm] = useState(null);

  const handleUpdatePwd = () => {
    setPwdError('');
    if (!curPwd) { setPwdError('Current password is required.'); return; }
    if (newPwd.length < 8) { setPwdError('New password must be at least 8 characters.'); return; }
    if (newPwd !== confirmPwd) { setPwdError('Passwords do not match.'); return; }
    setPwdSaved(true);
    setCurPwd(''); setNewPwd(''); setConfirmPwd('');
    setTimeout(() => setPwdSaved(false), 2500);
  };

  const handleSignOut = (id) => {
    setSessions(prev => prev.filter(s => s.id !== id));
    setSignOutConfirm(null);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* 2FA */}
      <div style={{ background: C.w, borderRadius: 14, border: `1px solid ${C.bdr}`, padding: mob ? '20px' : '28px 32px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
          <div style={{ maxWidth: 580 }}>
            <h3 style={{ fontSize: mob ? 15 : 17, fontWeight: 700, marginBottom: 4 }}>Two-Factor Authentication</h3>
            <p style={{ fontSize: 13, color: C.t2, lineHeight: 1.6 }}>
              Add an extra layer of security to your account by requiring a verification code in addition to your password.
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: twoFA ? C.green : C.t3, textTransform: 'uppercase', letterSpacing: '.06em' }}>
              Status: {twoFA ? 'Enabled' : 'Disabled'}
            </span>
            <Toggle on={twoFA} onToggle={() => setTwoFA(!twoFA)} />
          </div>
        </div>
      </div>

      {/* Password Management */}
      <div style={{ background: C.w, borderRadius: 14, border: `1px solid ${C.bdr}`, padding: mob ? '20px' : '28px 32px' }}>
        <h3 style={{ fontSize: mob ? 15 : 17, fontWeight: 700, marginBottom: mob ? 16 : 24 }}>Password Management</h3>
        <div style={{ maxWidth: mob ? '100%' : 480, display: 'flex', flexDirection: 'column', gap: mob ? 14 : 18 }}>
          <PwdField label="Current Password" value={curPwd} onChange={setCurPwd} show={showCur} onToggle={() => setShowCur(!showCur)} />
          <PwdField label="New Password" value={newPwd} onChange={setNewPwd} show={showNew} onToggle={() => setShowNew(!showNew)} />
          <PwdField label="Confirm New Password" value={confirmPwd} onChange={setConfirmPwd} show={showConfirm} onToggle={() => setShowConfirm(!showConfirm)} />

          {pwdError && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, background: '#fef2f2', border: `1px solid ${C.red}20` }}>
              <Icon name="error" size={16} style={{ color: C.red }} />
              <span style={{ fontSize: 12, color: C.red, fontWeight: 600 }}>{pwdError}</span>
            </div>
          )}

          <button onClick={handleUpdatePwd} style={{
            padding: '12px 28px', borderRadius: 10, border: 'none',
            background: pwdSaved ? C.green : C.primary, color: '#fff',
            fontSize: 13, fontWeight: 700, cursor: 'pointer', alignSelf: 'flex-start',
            transition: 'background .2s', display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <Icon name={pwdSaved ? 'check_circle' : 'lock'} size={16} />
            {pwdSaved ? 'Password Updated!' : 'Update Password'}
          </button>
        </div>
      </div>

      {/* Active Sessions */}
      <div style={{ background: C.w, borderRadius: 14, border: `1px solid ${C.bdr}`, padding: mob ? '20px' : '28px 32px' }}>
        <h3 style={{ fontSize: mob ? 15 : 17, fontWeight: 700, marginBottom: mob ? 16 : 24 }}>Active Sessions</h3>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {sessions.map((s, i) => (
            <div key={s.id} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '16px 0',
              borderTop: i > 0 ? `1px solid ${C.bdr}` : 'none',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ width: 42, height: 42, borderRadius: 10, background: '#f5f3f1', display: 'grid', placeItems: 'center' }}>
                  <Icon name={s.icon} size={22} style={{ color: C.t2 }} />
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>{mob ? s.device : `${s.device} — ${s.location}`}</div>
                  {mob && <div style={{ fontSize: 11, color: C.t3 }}>{s.location}</div>}
                  <div style={{ fontSize: 11, fontWeight: 600, color: s.active ? C.green : C.t3, marginTop: 2 }}>{s.time}</div>
                </div>
              </div>
              <button onClick={() => setSignOutConfirm(s.id)} style={{
                padding: '6px 16px', borderRadius: 8,
                border: `1px solid ${C.bdr}`, background: C.w,
                fontSize: 11, fontWeight: 700, color: C.t2, cursor: 'pointer',
              }}>Sign Out</button>
            </div>
          ))}
          {sessions.length === 0 && (
            <p style={{ fontSize: 13, color: C.t3, textAlign: 'center', padding: 20 }}>No active sessions.</p>
          )}
        </div>
      </div>

      {/* Sign Out Confirmation */}
      <Backdrop open={!!signOutConfirm} onClose={() => setSignOutConfirm(null)} width={400}>
        <div style={{ padding: '28px 32px', textAlign: 'center' }}>
          <div style={{ width: 52, height: 52, borderRadius: '50%', background: '#fef2f2', display: 'grid', placeItems: 'center', margin: '0 auto 16px' }}>
            <Icon name="logout" size={24} style={{ color: C.red }} />
          </div>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>Sign Out Session?</h3>
          <p style={{ fontSize: 13, color: C.t2, lineHeight: 1.6, marginBottom: 20 }}>
            This will terminate the session on that device. The user will need to log in again.
          </p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
            <button onClick={() => setSignOutConfirm(null)} style={{ padding: '10px 24px', borderRadius: 8, border: `1px solid ${C.bdr}`, background: C.w, fontSize: 13, fontWeight: 700, cursor: 'pointer', color: C.t2 }}>Cancel</button>
            <button onClick={() => handleSignOut(signOutConfirm)} style={{ padding: '10px 24px', borderRadius: 8, border: 'none', background: C.red, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Sign Out</button>
          </div>
        </div>
      </Backdrop>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   PREFERENCES TAB
   ═══════════════════════════════════════════════════════════ */
function PreferencesTab({ mob, compact }) {
  const [emailNotif, setEmailNotif]       = useState(true);
  const [pushNotif, setPushNotif]         = useState(false);
  const [engagementAlert, setEngagement]  = useState(true);
  const [language, setLanguage]           = useState('English');
  const [timezone, setTimezone]           = useState('GMT+07:00 Bangkok, Hanoi, Jakarta');
  const [theme, setTheme]                 = useState('light');
  const [prefSaved, setPrefSaved]         = useState(false);

  const handleSave = () => { setPrefSaved(true); setTimeout(() => setPrefSaved(false), 2500); };
  const handleDiscard = () => {
    setEmailNotif(true); setPushNotif(false); setEngagement(true);
    setLanguage('English'); setTimezone('GMT+07:00 Bangkok, Hanoi, Jakarta'); setTheme('light');
  };

  const themes = [
    { id: 'light',  icon: 'light_mode',      label: 'Light' },
    { id: 'dark',   icon: 'dark_mode',        label: 'Dark' },
    { id: 'system', icon: 'desktop_windows',  label: 'System' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Notifications */}
      <div style={{ background: C.w, borderRadius: 14, border: `1px solid ${C.bdr}`, overflow: 'hidden' }}>
        <div style={{ padding: mob ? '12px 16px' : '14px 28px', background: '#faf9f8', borderBottom: `1px solid ${C.bdr}` }}>
          <h4 style={{ fontSize: 10, fontWeight: 700, color: C.primary, textTransform: 'uppercase', letterSpacing: '.1em' }}>Notifications</h4>
        </div>
        {[
          ['Email Notifications', 'Receive daily activity summaries and account alerts.', emailNotif, () => setEmailNotif(!emailNotif)],
          ['Push Notifications', 'Get real-time updates in your browser.', pushNotif, () => setPushNotif(!pushNotif)],
          ['Engagement Alerts', 'Notify when a new lead enters the pipeline.', engagementAlert, () => setEngagement(!engagementAlert)],
        ].map(([title, desc, on, toggle], i) => (
          <div key={title} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: mob ? '14px 16px' : '18px 28px',
            borderBottom: i < 2 ? `1px solid ${C.bdr}` : 'none',
          }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700 }}>{title}</div>
              <div style={{ fontSize: 12, color: C.t2, marginTop: 2 }}>{desc}</div>
            </div>
            <Toggle on={on} onToggle={toggle} />
          </div>
        ))}
      </div>

      {/* Regional & Language */}
      <div style={{ background: C.w, borderRadius: 14, border: `1px solid ${C.bdr}`, overflow: 'hidden' }}>
        <div style={{ padding: mob ? '12px 16px' : '14px 28px', background: '#faf9f8', borderBottom: `1px solid ${C.bdr}` }}>
          <h4 style={{ fontSize: 10, fontWeight: 700, color: C.primary, textTransform: 'uppercase', letterSpacing: '.1em' }}>Regional & Language</h4>
        </div>
        <div style={{ padding: mob ? '16px' : '24px 28px', display: 'grid', gridTemplateColumns: mob ? '1fr' : '1fr 1fr', gap: mob ? 14 : 20 }}>
          <div>
            <Label>System Language</Label>
            <SelectBox value={language} onChange={setLanguage} options={['English', 'Spanish', 'French', 'German', 'Bahasa Indonesia', 'Japanese']} />
          </div>
          <div>
            <Label>Timezone</Label>
            <SelectBox value={timezone} onChange={setTimezone} options={[
              'GMT+07:00 Bangkok, Hanoi, Jakarta', 'GMT-08:00 Pacific Time (US & Canada)',
              'GMT+00:00 London, Casablanca', 'GMT+09:00 Tokyo, Seoul', 'GMT+08:00 Singapore, Kuala Lumpur',
            ]} />
          </div>
        </div>
      </div>

      {/* Appearance */}
      <div style={{ background: C.w, borderRadius: 14, border: `1px solid ${C.bdr}`, overflow: 'hidden' }}>
        <div style={{ padding: mob ? '12px 16px' : '14px 28px', background: '#faf9f8', borderBottom: `1px solid ${C.bdr}` }}>
          <h4 style={{ fontSize: 10, fontWeight: 700, color: C.primary, textTransform: 'uppercase', letterSpacing: '.1em' }}>Appearance</h4>
        </div>
        <div style={{ padding: mob ? '16px' : '24px 28px' }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 14 }}>Theme Mode</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
            {themes.map(t => {
              const sel = theme === t.id;
              return (
                <div key={t.id} onClick={() => setTheme(t.id)} style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                  padding: '20px 16px', borderRadius: 12, cursor: 'pointer',
                  border: `2px solid ${sel ? C.primary : C.bdr}`,
                  background: sel ? C.pL : '#faf9f8',
                  transition: 'all .15s',
                }}>
                  <Icon name={t.icon} size={28} style={{ color: sel ? C.primary : C.t3 }} />
                  <span style={{ fontSize: 12, fontWeight: 600, color: sel ? C.primary : C.t2 }}>{t.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Footer actions */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '16px 0', borderTop: `1px solid ${C.bdr}`, marginTop: 4,
      }}>
        <button onClick={handleDiscard} style={{
          padding: mob ? '8px 16px' : '10px 24px', borderRadius: 8, border: `1px solid ${C.bdr}`,
          background: C.w, fontSize: mob ? 12 : 13, fontWeight: 700, color: C.t2, cursor: 'pointer',
        }}>{mob ? 'Discard' : 'Discard Changes'}</button>
        <button onClick={handleSave} style={{
          padding: '10px 28px', borderRadius: 8, border: 'none',
          background: prefSaved ? C.green : C.primary, color: '#fff',
          fontSize: 13, fontWeight: 700, cursor: 'pointer', transition: 'background .2s',
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <Icon name={prefSaved ? 'check_circle' : 'save'} size={16} />
          {prefSaved ? 'Saved!' : 'Save Preferences'}
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   BILLING TAB
   ═══════════════════════════════════════════════════════════ */
function BillingTab({ mob, compact }) {
  const [businessName, setBusinessName] = useState('Nieramind Labs Inc.');
  const [taxId, setTaxId]               = useState('US-987654321');
  const [billingAddr, setBillingAddr]   = useState('123 AI Boulevard, Suite 500, San Francisco, CA 94107');
  const [upgradeModal, setUpgradeModal] = useState(false);
  const [editCard, setEditCard]         = useState(false);

  const INVOICES = [
    { date: 'Sep 24, 2024', id: 'INV-2024-009', amount: '$49.00', status: 'Paid' },
    { date: 'Aug 24, 2024', id: 'INV-2024-008', amount: '$49.00', status: 'Paid' },
    { date: 'Jul 24, 2024', id: 'INV-2024-007', amount: '$49.00', status: 'Paid' },
    { date: 'Jun 24, 2024', id: 'INV-2024-006', amount: '$49.00', status: 'Paid' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* 2-column grid */}
      <div style={{ display: 'grid', gridTemplateColumns: compact ? '1fr' : '1fr 1fr', gap: compact ? 16 : 24 }}>
        {/* Current Plan */}
        <div style={{ background: C.w, borderRadius: 14, border: `1px solid ${C.bdr}`, padding: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 16 }}>
            <div>
              <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Current Plan</h3>
              <p style={{ fontSize: 22, fontWeight: 800, color: C.primary }}>Professional Plan</p>
            </div>
            <span style={{ fontSize: 10, fontWeight: 700, color: C.green, background: '#ecfdf5', padding: '4px 10px', borderRadius: 99, textTransform: 'uppercase' }}>Active</span>
          </div>
          <div style={{ marginBottom: 20 }}>
            <p style={{ fontSize: 14, fontWeight: 700 }}>$49/mo <span style={{ fontSize: 12, fontWeight: 500, color: C.t3 }}>• Billed monthly</span></p>
            <p style={{ fontSize: 12, color: C.t3, marginTop: 2 }}>Next renewal: Oct 24, 2024</p>
          </div>
          <button onClick={() => setUpgradeModal(true)} style={{
            width: '100%', padding: '12px 0', borderRadius: 10, border: 'none',
            background: C.primary, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer',
          }}>Upgrade Plan</button>
        </div>

        {/* Payment Method */}
        <div style={{ background: C.w, borderRadius: 14, border: `1px solid ${C.bdr}`, padding: 24 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Payment Method</h3>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px',
            border: `1px solid ${C.bdr}`, borderRadius: 10, background: '#faf9f8', marginBottom: 16,
          }}>
            <div style={{ width: 36, height: 36, borderRadius: 8, background: C.w, border: `1px solid ${C.bdr}`, display: 'grid', placeItems: 'center' }}>
              <Icon name="credit_card" size={20} style={{ color: C.indigo }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700 }}>Visa ending in 4242</div>
              <div style={{ fontSize: 11, color: C.t3 }}>Expiry 12/26</div>
            </div>
            <button onClick={() => setEditCard(true)} style={{
              padding: '5px 14px', borderRadius: 6, border: `1px solid ${C.bdr}`,
              background: C.w, fontSize: 11, fontWeight: 700, cursor: 'pointer',
            }}>Edit</button>
          </div>
          <button style={{
            background: 'none', border: 'none', color: C.primary,
            fontSize: 12, fontWeight: 700, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 4, padding: 0,
          }}>
            <Icon name="add_circle" size={16} /> Add New Payment Method
          </button>
        </div>

        {/* Plan Usage */}
        <div style={{ background: C.w, borderRadius: 14, border: `1px solid ${C.bdr}`, padding: 24 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 20 }}>Plan Usage</h3>
          {[
            ['Total Documents', 847, 1000],
            ['API Requests', '1.2M', '2M', 60],
          ].map(([label, used, total, pctOverride]) => {
            const pct = pctOverride || (typeof used === 'number' ? (used / total * 100) : 60);
            return (
              <div key={label} style={{ marginBottom: 18 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
                  <span style={{ fontWeight: 600 }}>{label}</span>
                  <span style={{ fontWeight: 700 }}>{typeof used === 'number' ? used.toLocaleString() : used} <span style={{ fontWeight: 400, color: C.t3 }}>/ {typeof total === 'number' ? total.toLocaleString() : total}</span></span>
                </div>
                <div style={{ height: 8, borderRadius: 99, background: '#f1eeec', overflow: 'hidden' }}>
                  <div style={{ height: '100%', borderRadius: 99, background: pct > 80 ? C.amber : C.green, width: `${pct}%`, transition: 'width .4s' }} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Billing Information */}
        <div style={{ background: C.w, borderRadius: 14, border: `1px solid ${C.bdr}`, padding: 24 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 20 }}>Billing Information</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <FieldInput label="Business Name" value={businessName} onChange={setBusinessName} />
            <FieldInput label="Tax ID / VAT" value={taxId} onChange={setTaxId} />
            <div>
              <Label>Billing Address</Label>
              <textarea value={billingAddr} onChange={e => setBillingAddr(e.target.value)} rows={2} style={{
                width: '100%', padding: '10px 14px', borderRadius: 10,
                border: `1px solid ${C.bdr}`, background: '#faf9f8',
                fontSize: 13, outline: 'none', color: C.t1, resize: 'vertical',
                fontFamily: 'inherit',
              }} />
            </div>
          </div>
        </div>
      </div>

      {/* Billing History Table */}
      <div style={{ background: C.w, borderRadius: 14, border: `1px solid ${C.bdr}`, overflow: 'hidden' }}>
        <div style={{ padding: mob ? '14px 16px' : '20px 24px', borderBottom: `1px solid ${C.bdr}` }}>
          <h3 style={{ fontSize: mob ? 14 : 15, fontWeight: 700 }}>Billing History</h3>
        </div>
        {mob ? (
          <div>
            {INVOICES.map((inv, i) => (
              <div key={i} style={{ padding: '12px 16px', borderBottom: i < INVOICES.length - 1 ? `1px solid ${C.bdr}` : 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>{inv.amount}</div>
                  <div style={{ fontSize: 11, color: C.t2, marginTop: 2 }}>{inv.date} · {inv.id}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 700, color: C.green, background: '#ecfdf5', padding: '3px 8px', borderRadius: 99 }}>
                    <span style={{ width: 5, height: 5, borderRadius: '50%', background: C.green }} /> {inv.status}
                  </span>
                  <button style={{ border: 'none', background: 'none', color: C.t3, cursor: 'pointer', padding: 4 }}>
                    <Icon name="download" size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#faf9f8', borderBottom: `1px solid ${C.bdr}` }}>
                {['Date', 'Invoice ID', 'Amount', 'Status', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '10px 24px', textAlign: h === 'Actions' ? 'right' : 'left', fontSize: 10, fontWeight: 700, color: C.t3, textTransform: 'uppercase', letterSpacing: '.06em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {INVOICES.map((inv, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${C.bdr}`, transition: 'background .1s' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#faf8f7'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ padding: '14px 24px', fontWeight: 600 }}>{inv.date}</td>
                  <td style={{ padding: '14px 24px', color: C.t2 }}>{inv.id}</td>
                  <td style={{ padding: '14px 24px', fontWeight: 700 }}>{inv.amount}</td>
                  <td style={{ padding: '14px 24px' }}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                      fontSize: 11, fontWeight: 700, color: C.green,
                      background: '#ecfdf5', padding: '3px 10px', borderRadius: 99,
                    }}>
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: C.green }} /> {inv.status}
                    </span>
                  </td>
                  <td style={{ padding: '14px 24px', textAlign: 'right' }}>
                    <button style={{ border: 'none', background: 'none', color: C.t3, cursor: 'pointer' }}>
                      <Icon name="download" size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Upgrade Modal */}
      <Backdrop open={upgradeModal} onClose={() => setUpgradeModal(false)} width={520}>
        <div style={{ padding: '28px 32px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h3 style={{ fontSize: 18, fontWeight: 700 }}>Upgrade Your Plan</h3>
            <Icon name="close" size={20} style={{ color: C.t3, cursor: 'pointer' }} onClick={() => setUpgradeModal(false)} />
          </div>
          {[
            { name: 'Starter', price: '$19/mo', desc: 'Up to 500 conversations/month', current: false },
            { name: 'Professional', price: '$49/mo', desc: 'Up to 5,000 conversations/month', current: true },
            { name: 'Enterprise', price: '$199/mo', desc: 'Unlimited conversations, SLA, SSO', current: false },
          ].map(plan => (
            <div key={plan.name} style={{
              padding: '16px 20px', borderRadius: 12, marginBottom: 10,
              border: `2px solid ${plan.current ? C.primary : C.bdr}`,
              background: plan.current ? C.pL : 'transparent',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700 }}>{plan.name}</div>
                <div style={{ fontSize: 12, color: C.t2, marginTop: 2 }}>{plan.desc}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: plan.current ? C.primary : C.t1 }}>{plan.price}</div>
                {plan.current && <span style={{ fontSize: 9, fontWeight: 700, color: C.primary, textTransform: 'uppercase' }}>Current Plan</span>}
              </div>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
            <button onClick={() => setUpgradeModal(false)} style={{ padding: '10px 20px', borderRadius: 8, border: `1px solid ${C.bdr}`, background: C.w, fontSize: 13, fontWeight: 700, cursor: 'pointer', color: C.t2 }}>Cancel</button>
            <button style={{ padding: '10px 24px', borderRadius: 8, border: 'none', background: C.primary, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Confirm Upgrade</button>
          </div>
        </div>
      </Backdrop>

      {/* Edit Card Modal */}
      <Backdrop open={editCard} onClose={() => setEditCard(false)} width={440}>
        <div style={{ padding: '28px 32px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700 }}>Edit Payment Method</h3>
            <Icon name="close" size={20} style={{ color: C.t3, cursor: 'pointer' }} onClick={() => setEditCard(false)} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <FieldInput label="Card Number" value="•••• •••• •••• 4242" disabled />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <FieldInput label="Expiry Date" value="12/26" />
              <FieldInput label="CVV" value="•••" />
            </div>
            <FieldInput label="Cardholder Name" value="Alex Rivera" />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
            <button onClick={() => setEditCard(false)} style={{ padding: '10px 20px', borderRadius: 8, border: `1px solid ${C.bdr}`, background: C.w, fontSize: 13, fontWeight: 700, cursor: 'pointer', color: C.t2 }}>Cancel</button>
            <button onClick={() => setEditCard(false)} style={{ padding: '10px 24px', borderRadius: 8, border: 'none', background: C.primary, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Save Card</button>
          </div>
        </div>
      </Backdrop>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Shared Tiny Components
   ═══════════════════════════════════════════════════════════ */
function Label({ children }) {
  return <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: C.t3, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>{children}</label>;
}

function FieldInput({ label, value, onChange, disabled = false, icon }) {
  return (
    <div>
      <Label>{label} {icon && <Icon name={icon} size={12} style={{ color: C.t3, verticalAlign: 'middle', marginLeft: 4 }} />}</Label>
      <input
        type="text" value={value} readOnly={disabled}
        onChange={onChange ? e => onChange(e.target.value) : undefined}
        style={{
          width: '100%', padding: '10px 14px', borderRadius: 10, boxSizing: 'border-box',
          border: `1px solid ${C.bdr}`, background: disabled ? '#f1eeec' : '#faf9f8',
          fontSize: 13, outline: 'none', color: disabled ? C.t3 : C.t1,
          cursor: disabled ? 'not-allowed' : 'text',
        }}
      />
    </div>
  );
}

function PwdField({ label, value, onChange, show, onToggle }) {
  return (
    <div>
      <Label>{label}</Label>
      <div style={{ position: 'relative' }}>
        <input
          type={show ? 'text' : 'password'} value={value}
          onChange={e => onChange(e.target.value)} placeholder="••••••••••••"
          style={{
            width: '100%', padding: '10px 42px 10px 14px', borderRadius: 10, boxSizing: 'border-box',
            border: `1px solid ${C.bdr}`, background: '#faf9f8',
            fontSize: 13, outline: 'none', color: C.t1,
          }}
        />
        <button onClick={onToggle} style={{
          position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
          border: 'none', background: 'none', cursor: 'pointer', padding: 2,
          color: show ? C.primary : C.t3,
        }}>
          <Icon name={show ? 'visibility_off' : 'visibility'} size={18} />
        </button>
      </div>
    </div>
  );
}

function SelectBox({ value, onChange, options }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} style={{
      width: '100%', padding: '10px 14px', borderRadius: 10, boxSizing: 'border-box',
      border: `1px solid ${C.bdr}`, background: '#faf9f8',
      fontSize: 13, outline: 'none', color: C.t1, appearance: 'none',
      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24'%3E%3Cpath fill='%23a8a29e' d='M7 10l5 5 5-5z'/%3E%3C/svg%3E")`,
      backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center',
    }}>
      {options.map(o => <option key={o}>{o}</option>)}
    </select>
  );
}

/* ═══════════════════════════════════════════════════════════
   MAIN USER SETTINGS PAGE
   ═══════════════════════════════════════════════════════════ */
export default function UserSettingsPage() {
  const { isMobile, isTablet, isDesktop, isWide } = useBreakpoint();
  const mob = isMobile;
  const compact = isMobile || isTablet;
  const pad = mob ? 16 : isTablet ? 24 : 32;

  const [activeTab, setActiveTab] = useState('Profile');
  const tabs = ['Profile', 'Security', 'Preferences', 'Billing'];

  return (
    <div style={{ height: '100%', overflow: 'auto' }}>
      <div style={{ padding: `${mob ? 16 : 32}px ${pad}px 48px`, maxWidth: 1100 }}>
        {/* Breadcrumb */}
        {!mob && <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, color: C.t3, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 6 }}>
          <span>Console</span>
          <Icon name="chevron_right" size={14} style={{ color: C.t3 }} />
          <span style={{ color: C.primary }}>User Settings</span>
        </div>}
        <h1 style={{ fontSize: mob ? 22 : 26, fontWeight: 800, letterSpacing: '-.02em', marginBottom: 4 }}>User Settings</h1>
        <p style={{ fontSize: 13, color: C.t2, marginBottom: mob ? 16 : 24 }}>{mob ? 'Manage your account and billing.' : 'Manage your account preferences, security settings, and billing information.'}</p>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: mob ? 0 : 32, borderBottom: `1px solid ${C.bdr}`, marginBottom: mob ? 20 : 28, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          {tabs.map(t => {
            const sel = activeTab === t;
            return (
              <button key={t} onClick={() => setActiveTab(t)} style={{
                padding: mob ? '10px 14px' : '14px 0', fontSize: mob ? 12 : 13, whiteSpace: 'nowrap', fontWeight: sel ? 700 : 500,
                color: sel ? C.primary : C.t3,
                background: 'none', border: 'none',
                borderBottom: `2px solid ${sel ? C.primary : 'transparent'}`,
                cursor: 'pointer', transition: 'color .12s',
              }}>{t}</button>
            );
          })}
        </div>

        {/* Tab content */}
        {activeTab === 'Profile' && <ProfileTab mob={mob} compact={compact} />}
        {activeTab === 'Security' && <SecurityTab mob={mob} compact={compact} />}
        {activeTab === 'Preferences' && <PreferencesTab mob={mob} compact={compact} />}
        {activeTab === 'Billing' && <BillingTab mob={mob} compact={compact} />}
      </div>
    </div>
  );
}
